import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import InsLoader from "../../../../components/InsLoader/InsLoader";
import InsButton from "../../../../components/InsButton/InsButton";
import StepCount from "../../../../components/StepCount/StepCount";
import { updateInfos } from "store/actions/createAccountAction";
import "./BrandsStep.scss";

export default function BrandsStep(props) {
  const {
    currentStep,
    maxSteps,
    onValidate,
    userInfo,
    brands,
    brandsLoading
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const config = useSelector((state) => state.config);
  const [selectedIds, setSelectedIds] = useState(() => new Set(userInfo?.brands || []));
  const [failedImages, setFailedImages] = useState(() => new Set());

  const isBrandsRequired = useMemo(() => {
    const brandConfig = config?.register?.brand?.find((item) => item.name === "brands");
    return brandConfig?.required === true;
  }, [config]);

  const isNextDisabled = useMemo(() => {
    return brands?.length > 0 && isBrandsRequired && selectedIds.size === 0;
  }, [isBrandsRequired, selectedIds]);

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const updatedIds = new Set(prev);
      if (updatedIds.has(id)) {
        updatedIds.delete(id);
      }
      else {
        updatedIds.add(id);
      }
      dispatch(updateInfos({ brands: Array.from(updatedIds) }));
      return updatedIds;
    });
  }, []);

  useEffect(() => {
    if (!Array.isArray(brands) || brands.length === 0) {
      selectedIds.size > 0 && setSelectedIds(new Set());
      failedImages.size > 0 && setFailedImages(new Set());
      return;
    }

    const available = new Set(brands.map((b) => b.brand_id));

    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        if (!available.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      const updatedIds = changed ? next : prev;
      dispatch(updateInfos({ brands: Array.from(updatedIds) }));
      return updatedIds;
    });

    setFailedImages((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        if (!available.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      const updatedIds = changed ? next : prev;
      return updatedIds;
    });
  }, [brands]);

  const sortedBrands = useMemo(() => {
    if (!Array.isArray(brands)) {
      return []
    };
    return [...brands].sort((a, b) =>
      a.brand_name.localeCompare(b.brand_name)
    );
  }, [brands]);

  const brandItems = useMemo(
    () =>
      sortedBrands.map((brand) => {
        const isSelected = selectedIds.has(brand.brand_id);
        const hasImageFailed = failedImages.has(brand.brand_id);

        return (
          <div
            key={brand.brand_id}
            role="button"
            tabIndex={0}
            className={`brandCard ${isSelected ? "selected" : ""} ${hasImageFailed ? "noImage" : ""}`}
            {...(hasImageFailed ? { "data-fallback": brand.brand_name } : {})}
            onClick={() => toggle(brand.brand_id)}
            aria-pressed={isSelected}
            title={brand.brand_name}
          >
            {!hasImageFailed ? (
              <img
                className="brandLogo"
                src={brand.logo}
                alt={brand.brand_name}
                onError={() =>
                  setFailedImages((prev) => {
                    const next = new Set(prev);
                    next.add(brand.brand_id);
                    return next;
                  })
                }
                onLoad={() =>
                  setFailedImages((prev) => {
                    if (!prev.has(brand.brand_id)) return prev;
                    const next = new Set(prev);
                    next.delete(brand.brand_id);
                    return next;
                  })
                }
              />
            ) : null}
          </div>
        );
      }),
    [brands, selectedIds, toggle, failedImages]
  );

  return (
    <form
      className="default-container col-flex"
      onSubmit={(e) => e.preventDefault()}
    >
      <StepCount step={currentStep} maxStepCount={maxSteps} />
      <div className="brandsHeader">
        <p className="bold-label centered">{t("brandStep_title")}</p>
        <p className="brandSubTitle">{
          <Trans
            i18nKey="brandStep_subtitle"
            defaults={"Select the brands you are working with. <br/> You can select multiple brands."}
            components={{
              br: <br />,
            }}
          />
        }
        </p>
      </div>
      <section className="brandsSection" aria-label="Select brands">
        <div className="brandsScroll">
          {brandsLoading ? (
            <div className="brandsLoading">
              <InsLoader />
            </div>
          ) : sortedBrands.length > 0 ? (
            <div className="brandGrid">{brandItems}</div>
          ) : (
            <div className="noBrandsSection">
              <p className="noBrandsLabel">{t("no_brands_for_retailer")}</p>
            </div>
          )}
        </div>
      </section>
      <div className="bottom-container">
        {isNextDisabled && (
          <div className="alert_msg mb10">{t("select_atleast_one_brand")}</div>
        )}
        <InsButton
          text={t("next")}
          onClick={() => onValidate({ brands: Array.from(selectedIds) })}
          disabled={isNextDisabled}
        />
      </div>
    </form>
  );
}